import { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { ThemeSet } from 'dashboard/models/theme-set';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setOmniSearch, setSortParams } from 'dashboard/redux/slices/theme-sets';
import { RootState } from 'dashboard/redux/store';
import {
  loadDynamicFlowInquiryTemplateList,
  updateOrgDefaultThemeSet,
} from 'dashboard/redux/thunk-actions/organization';
import {
  archiveThemeSet,
  cloneThemeSet,
  createThemeSet,
  loadThemeSets,
} from 'dashboard/redux/thunk-actions/theme-sets';
import DashboardThemeSetsView from 'dashboard/views/DashboardThemeSetsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardThemeSetsRoute = (props: PropsFromRedux) => {
  const displayNetworkErrorMessage = () =>
    props.showMessage('We are experiencing network issues. Please try again.', MessageType.Failure);

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.loadThemeSets, props.indexFilter],
  );

  const { loading: isThemeSetLoading } = useApiLoader({
    loader: async () => {
      await Promise.all([
        props.loadThemeSets({ ...props.indexFilter, abortSignal: abortController.signal }),
        props.loadDynamicFlowInquiryTemplateList(),
      ]);
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load themes, please try again', MessageType.Failure);
      }
    },
    onNetworkError: displayNetworkErrorMessage,
    deps: [props.loadThemeSets, props.showMessage, props.indexFilter],
  });

  const { callback: createThemeSet, error: createThemeSetError } = useApiCallback(
    async (themeSetName: string) => {
      await props.createThemeSet(themeSetName);
    },
    [props.createThemeSet],
  );

  useApiErrorHandler({
    error: createThemeSetError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create Theme, please try again', MessageType.Failure);
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });

  const { callback: cloneThemeSet, error: cloneThemeSetError } = useApiCallback(
    async (themeSet: ThemeSet) => {
      await props.cloneThemeSet(themeSet);
    },
    [props.cloneThemeSet],
  );

  useApiErrorHandler({
    error: cloneThemeSetError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to duplicate Theme, please try again', MessageType.Failure);
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });

  const { callback: archiveThemeSet, error: archiveThemeSetError } = useApiCallback(
    async (themeSet: ThemeSet) => {
      await props.archiveThemeSet(themeSet);
    },
    [props.archiveThemeSet],
  );

  useApiErrorHandler({
    error: archiveThemeSetError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to archive Theme, please try again', MessageType.Failure);
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });

  const { callback: updateOrgDefaultThemeSet, error: updateOrgDefaultThemeSetError } =
    useApiCallback(
      async (themeSet: ThemeSet) => {
        await props.updateOrgDefaultThemeSet(themeSet.id);
        props.showMessage('Successfully set organization default theme', MessageType.Success);
      },
      [props.updateOrgDefaultThemeSet],
    );

  useApiErrorHandler({
    error: updateOrgDefaultThemeSetError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update Organization default theme. Please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });

  const isThemeSetFrontendEnabled = useFeatureFlag('dashboard:fle-theme-set-index-and-editor');

  return isThemeSetFrontendEnabled ? (
    <DashboardThemeSetsView
      themeSets={props.themeSets}
      indexFilter={props.indexFilter}
      inquiryTemplates={props.inquiryTemplates}
      createThemeSet={createThemeSet}
      cloneThemeSet={cloneThemeSet}
      archiveThemeSet={archiveThemeSet}
      updateOrgDefaultThemeSet={updateOrgDefaultThemeSet}
      setOmniSearch={(omni: string) => props.setOmniSearch(omni)}
      setSortParams={(sortParams: SortParams) => props.setSortParams(sortParams)}
      isLoading={isThemeSetLoading}
    />
  ) : null;
};

const mapStateToProps = (state: RootState) => {
  return {
    themeSets: state.themeSets.themeSets,
    indexFilter: state.themeSets.filters,
    inquiryTemplates: state.organization.inquiryTemplates,
  };
};

const mapDispatchToProps = {
  loadDynamicFlowInquiryTemplateList,
  loadThemeSets,
  createThemeSet,
  cloneThemeSet,
  archiveThemeSet,
  updateOrgDefaultThemeSet,
  showMessage,
  setOmniSearch,
  setSortParams,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardThemeSetsRoute);
